import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { VideoData } from '@lumen5/luminary';

import type { AIVoice, Blueprint, Template } from 'js/src/client/types.gen';
import { NarrativeThreadEnum } from 'js/src/client/types.gen';
import { useSendMessage } from 'js/src/components/sketch/SketchContext';
import { VideoDraftAspectRatioModal } from 'js/src/components/video-planner/VideoDraftAspectRatioModal';
import { VideoDraftDurationModal } from 'js/src/components/video-planner/VideoDraftDurationModal';
import type { VideoInfoChipProps } from 'js/src/components/video-planner/VideoInfoChip';
import { VideoInfoChip } from 'js/src/components/video-planner/VideoInfoChip';
import { ensureAIVoiceLoaded, getAIVoiceById } from 'js/src/data/ai-voices-and-avatars';
import { getBlueprint } from 'js/src/data/blueprints/blueprints-selectors';
import { getBrandKitById } from 'js/src/data/brand-kits/brand-kits-selectors';
import type {
    SketchBlueprintsModalContext,
    TemplatePickerModalContext,
    VoiceSelectionModalContext,
} from 'js/src/data/modals';
import { hideModal, MODAL_IDS, showModal, VOICE_SELECTION_MODAL_SOURCE } from 'js/src/data/modals';
import { clearBrandKit } from 'js/src/data/sketch-controls/sketch-controls-slice';
import { getTemplateById } from 'js/src/data/templates';
import { getVideoDraftById } from 'js/src/data/video-planner';
import {
    formatIncompatibilitiesForAI,
    getVideoPlanIncompatibilities,
} from 'js/src/data/video-planner/video-plan-compatibility';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';

const CHIP_REVEAL_MIN_MS = 350;
const CHIP_REVEAL_MAX_MS = 800;
const MAX_CHIPS_TO_REVEAL = 99999;

interface VideoInfoChipsProps {
    videoDraftId: string;
    isActive: boolean;
}

export const VideoInfoChips: React.FC<VideoInfoChipsProps> = ({ videoDraftId, isActive }) => {
    const dispatch = useAppDispatch();
    const sendMessage = useSendMessage();
    const videoDraft = useSelector((state: RootState) => getVideoDraftById(state, videoDraftId));

    const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);
    const [isAspectRatioModalOpen, setIsAspectRatioModalOpen] = useState(false);
    const [visibleChipsCount, setVisibleChipsCount] = useState(0);

    const isGenerating = videoDraft?.isGenerating ?? false;

    // Progressive chip reveal during loading with random intervals
    useEffect(() => {
        if (isGenerating) {
            // Reset and start progressive reveal
            setVisibleChipsCount(1);
            let cancelled = false;

            const scheduleNextReveal = () => {
                const randomDelay =
                    CHIP_REVEAL_MIN_MS + Math.random() * (CHIP_REVEAL_MAX_MS - CHIP_REVEAL_MIN_MS);
                setTimeout(() => {
                    if (cancelled) return;
                    setVisibleChipsCount((prev) => {
                        const next = prev + 1;
                        if (next < 10) {
                            // Keep revealing up to a reasonable max
                            scheduleNextReveal();
                        }
                        return next;
                    });
                }, randomDelay);
            };

            scheduleNextReveal();
            return () => {
                cancelled = true;
            };
        } else {
            // Show all chips when finished generating
            setVisibleChipsCount(MAX_CHIPS_TO_REVEAL);
        }
    }, [isGenerating]);

    // Get values from video plan only
    const videoPlan = videoDraft?.videoPlan;

    const durationTargetSeconds = videoPlan?.duration_target_seconds ?? null;
    const voiceSlug = videoPlan?.ai_voice ?? null;
    const templateId = videoPlan?.template_id ?? null;
    // aspect ratio can change, so use the draft's current value if available
    const aspectRatio = videoDraft?.luminary.aspectRatio ?? videoPlan?.aspect_ratio ?? null;
    const blueprintId = videoPlan?.blueprint_id ?? null;

    const voice = useSelector((state: RootState) =>
        voiceSlug ? getAIVoiceById(state, voiceSlug) : null,
    );
    const template = useSelector((state: RootState) =>
        templateId ? getTemplateById(state, templateId) : undefined,
    );
    const blueprint = useSelector((state: RootState) => getBlueprint(state, blueprintId));

    // Ensure the voice is loaded in Redux (it may not have been fetched yet)
    useEffect(() => {
        if (voiceSlug && !voice) {
            dispatch(ensureAIVoiceLoaded(voiceSlug));
        }
    }, [dispatch, voiceSlug, voice]);

    // Brand kit from video draft (not video plan, since brand kit is applied separately)
    const brandKitId = videoDraft?.brandKitId ?? null;
    const brandKit = useSelector((state: RootState) =>
        brandKitId ? getBrandKitById(state, brandKitId) : undefined,
    );

    // Videos built from uploaded audio/video have voiceover driven by the
    // user's own recording, not an AI voice. The voice chip doesn't apply —
    // changing the voice would require re-recording.
    const isUploadedVoiceover =
        videoDraft?.creationToolName === 'create_video_from_voiceover_asset_plan';
    const hasVoiceover = !!voiceSlug || isUploadedVoiceover;

    const handleVoiceClick = useCallback(() => {
        if (!videoDraft || !sendMessage) return;

        const context: VoiceSelectionModalContext = {
            source: VOICE_SELECTION_MODAL_SOURCE.SCRIPT_EDIT_PAGE,
            initialSelectedVoiceId: voiceSlug,
            onApply: (selectedVoice: AIVoice) => {
                const voiceName = selectedVoice.display_name;

                if (hasVoiceover) {
                    void sendMessage(`Change the AI voice to ${voiceName}`);
                    analytics.track('Sketch: Video Info Chip - Change Voice', {
                        video_title: videoDraft.name,
                        ai_voice: selectedVoice.slug,
                    });
                } else {
                    void sendMessage(`Add voiceover with voice ${voiceName}`);
                    analytics.track('Sketch: Video Info Chip - Add Voiceover', {
                        video_title: videoDraft.name,
                        ai_voice: selectedVoice.slug,
                    });
                }

                dispatch(hideModal());
            },
            initialVoiceSpeed: 1,
            onRemoveVoiceover: hasVoiceover
                ? () => {
                      void sendMessage('Remove voiceover');

                      analytics.track('Sketch: Video Info Chip - Remove Voiceover', {
                          video_title: videoDraft.name,
                      });

                      dispatch(hideModal());
                  }
                : undefined,
        };

        dispatch(
            showModal({
                modal_id: MODAL_IDS.AI_VOICE_SELECTION,
                modal_context: ImmutableFromJS(context),
            }),
        );
    }, [videoDraft, sendMessage, voiceSlug, hasVoiceover, dispatch]);

    const handleDurationClick = useCallback(() => {
        setIsDurationModalOpen(true);
    }, []);

    const handleCloseDurationModal = useCallback(() => {
        setIsDurationModalOpen(false);
    }, []);

    const handleAspectRatioClick = useCallback(() => {
        setIsAspectRatioModalOpen(true);
    }, []);

    const handleCloseAspectRatioModal = useCallback(() => {
        setIsAspectRatioModalOpen(false);
    }, []);

    const handleTemplateClick = useCallback(() => {
        if (!videoDraft || !sendMessage) return;

        const context: TemplatePickerModalContext = {
            onSelect: (selectedTemplate: ImmutableMap<Template>) => {
                const selectedTemplateName = selectedTemplate.get('display_name');

                // Check for incompatibilities with current video plan
                const incompatibilities = getVideoPlanIncompatibilities(
                    { type: 'template', newValue: selectedTemplate },
                    { aspectRatio },
                );
                const incompatibilitiesText = formatIncompatibilitiesForAI(incompatibilities);

                const additionalInfo = incompatibilitiesText
                    ? `Template ID: ${selectedTemplate.get('id')}\n${incompatibilitiesText}`
                    : `Template ID: ${selectedTemplate.get('id')}`;

                void sendMessage(
                    `Change the template to ${selectedTemplateName}\n<additional_info>\n${additionalInfo}</additional_info>`,
                );
                analytics.track('Sketch: Video Info Chip - Change Template', {
                    video_title: videoDraft.name,
                    template_id: selectedTemplate.get('id'),
                });
            },
        };

        dispatch(
            showModal({
                modal_id: MODAL_IDS.TEMPLATE_PICKER,
                modal_context: ImmutableFromJS(context),
            }),
        );
    }, [videoDraft, sendMessage, dispatch, aspectRatio]);

    const handleBlueprintClick = useCallback(() => {
        if (!videoDraft || !sendMessage) return;

        // Determine current narrative thread based on voiceover setting
        const currentNarrativeThread = hasVoiceover
            ? NarrativeThreadEnum.VOICEOVER
            : NarrativeThreadEnum.TEXT;

        const context: SketchBlueprintsModalContext = {
            onSelect: (selectedBlueprint: Blueprint) => {
                // Check for incompatibilities with current video plan
                const incompatibilities = getVideoPlanIncompatibilities(
                    { type: 'blueprint', newValue: selectedBlueprint },
                    { template, narrativeThread: currentNarrativeThread, voiceSlug },
                );
                const incompatibilitiesText = formatIncompatibilitiesForAI(incompatibilities);

                const additionalInfo = incompatibilitiesText
                    ? `Blueprint ID: ${selectedBlueprint.id}\n${incompatibilitiesText}`
                    : `Blueprint ID: ${selectedBlueprint.id}`;

                void sendMessage(
                    `Use blueprint ${selectedBlueprint.name}\n<additional_info>\n${additionalInfo}</additional_info>`,
                );
                analytics.track('Sketch: Video Info Chip - Change Blueprint', {
                    video_title: videoDraft.name,
                    blueprint_id: selectedBlueprint.id,
                });
            },
        };

        dispatch(
            showModal({
                modal_id: MODAL_IDS.SKETCH_BLUEPRINTS,
                modal_context: ImmutableFromJS(context),
            }),
        );
    }, [videoDraft, sendMessage, dispatch, hasVoiceover, template, voiceSlug]);

    const handleBrandKitClick = useCallback(() => {
        if (!videoDraft || !sendMessage) return;

        // Clear brand kit from Redux so the next video won't have it applied
        dispatch(clearBrandKit());
        // Ask the AI to recreate the video without the brand kit
        void sendMessage(
            'Recreate this video without brand kit\n<instructions>The brand kit has been removed. Recreate the video with the same settings but without any brand kit applied.</instructions>',
        );
        analytics.track('Sketch: Video Info Chip - Remove Brand Kit', {
            video_title: videoDraft.name,
            brand_kit_id: brandKitId,
        });
    }, [videoDraft, sendMessage, brandKitId, dispatch]);

    if (!videoDraft) {
        return null;
    }

    if (!sendMessage) {
        return null;
    }

    const templateName = template?.get('display_name') || 'Template';
    const voiceName = voice?.display_name;
    const blueprintName = blueprint?.name;
    const brandKitName = brandKit?.get('name');

    const canInteract = isActive;

    // Build chips array - only include chips where video plan specifies the value
    const chips: Array<VideoInfoChipProps & { key: string }> = [];

    // Voiceover chip - always show, with "no voiceover" state when not specified.
    // Skipped for uploaded-voiceover videos, where the audio is the user's own
    // recording and swapping to an AI voice isn't a meaningful action here.
    if (!isUploadedVoiceover) {
        chips.push({
            key: 'voice',
            icon: voiceSlug ? 'record_voice_over' : 'voice_over_off',
            label: voiceSlug ? `Voiceover: ${voiceName ?? 'Loading...'}` : 'No voiceover',
            onClick: canInteract ? handleVoiceClick : undefined,
            tooltip: voiceSlug ? 'Change or remove the AI voiceover' : 'Add an AI voiceover',
            testId: 'video-info-chip-voice',
        });
    }

    // Duration chip - only show if video plan specifies duration_target_seconds
    if (durationTargetSeconds !== null) {
        chips.push({
            key: 'duration',
            icon: 'schedule',
            label: `${durationTargetSeconds}s`,
            onClick: canInteract ? handleDurationClick : undefined,
            tooltip: 'Adjust the target duration',
            testId: 'video-info-chip-duration',
        });
    }

    // Aspect ratio chip - only show if video plan specifies aspect_ratio
    if (aspectRatio) {
        chips.push({
            key: 'aspect-ratio',
            icon: 'aspect_ratio',
            label: VideoData.getAspectRatioDisplayName(aspectRatio),
            onClick: canInteract ? handleAspectRatioClick : undefined,
            tooltip: 'Change the format',
            testId: 'video-info-chip-aspect-ratio',
        });
    }

    // Template chip - only show if video plan specifies template_id
    if (templateId) {
        chips.push({
            key: 'template',
            icon: 'dashboard',
            label: templateName,
            onClick: canInteract ? handleTemplateClick : undefined,
            tooltip: 'Change the theme',
            testId: 'video-info-chip-template',
        });
    }

    // Blueprint chip - always show, with "no blueprint" state when not specified
    chips.push({
        key: 'blueprint',
        icon: 'auto_awesome',
        label: blueprintName ?? 'No blueprint',
        onClick: canInteract ? handleBlueprintClick : undefined,
        tooltip: blueprintId ? 'Change the blueprint' : 'Apply a blueprint for consistent styling',
        testId: 'video-info-chip-blueprint',
    });

    // Brand kit chip - only show if a brand kit is applied
    if (brandKitId && brandKitName) {
        chips.push({
            key: 'brand-kit',
            icon: 'brush',
            label: brandKitName,
            onClick: canInteract ? handleBrandKitClick : undefined,
            tooltip: 'Click to remove the brand kit',
            testId: 'video-info-chip-brand-kit',
        });
    }

    return (
        <div className="d-flex flex-wrap gap-1 mt-l5-12 me-l5-8">
            {chips.slice(0, visibleChipsCount).map((chip) => (
                <VideoInfoChip
                    key={chip.key}
                    icon={chip.icon}
                    label={chip.label}
                    onClick={chip.onClick}
                    disabled={!isActive}
                    tooltip={isActive ? chip.tooltip : undefined}
                    testId={chip.testId}
                />
            ))}
            <VideoDraftDurationModal
                videoDraftId={videoDraftId}
                sendMessage={sendMessage}
                isOpen={isDurationModalOpen}
                onClose={handleCloseDurationModal}
            />
            <VideoDraftAspectRatioModal
                videoDraftId={videoDraftId}
                sendMessage={sendMessage}
                isOpen={isAspectRatioModalOpen}
                onClose={handleCloseAspectRatioModal}
            />
        </div>
    );
};
